import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Building, Check } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import type { IMySpaceCompany } from "@/types/(my-space)/companies.interface";

interface CompanyCardProps {
  company: IMySpaceCompany;
  /** Only super admins can switch — everyone else sees the list read-only. */
  canSwitch: boolean;
  /** True while this row's own switch is in flight. */
  isSwitchingThis: boolean;
  /** True while any row's switch is in flight — locks the whole list. */
  isSwitchingAny: boolean;
  onSwitch: (companyId: number) => void;
}

/** `activeEmployeesCount` arrives as a string; "0" must not read as "0 active employees". */
const employeeSummary = (
  count: string | number | null | undefined,
  t: TFunction,
) => {
  const parsed = Number(count);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return t("common:chrome.active_employee_count", {
    count: parsed,
    formatted: parsed.toLocaleString(),
  });
};

export const CompanyCard = ({
  company,
  canSwitch,
  isSwitchingThis,
  isSwitchingAny,
  onSwitch,
}: CompanyCardProps) => {
  const { t } = useTranslation(["common"]);
  const { id, name, logoIcon, isCurrentCompany } = company;
  const summary = employeeSummary(company.activeEmployeesCount, t);

  return (
    <li
      // `order-*` floats the active company to the top without re-sorting the
      // API response (which would fight RTK's cached array identity).
      className={cn(
        "group relative flex items-center gap-3 overflow-hidden rounded-sm border p-2.5 transition-all duration-200",
        isCurrentCompany
          ? "glow-primary sheen order-1 border-primary/40 bg-primary-muted"
          : "order-2 border-border bg-surface hover:border-primary/25 hover:bg-muted/40",
      )}
      aria-current={isCurrentCompany ? "true" : undefined}
    >
      <Avatar
        className={cn(
          "size-10 shrink-0 rounded-xs ring-1 transition-shadow",
          isCurrentCompany ? "ring-primary/30" : "ring-border",
        )}
      >
        <AvatarImage src={logoIcon} alt="" />
        <AvatarFallback className="rounded-xs">
          <Building size={16} aria-hidden />
        </AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium capitalize" title={name}>
          {name}
        </p>
        {summary && (
          <p className="truncate text-xs text-muted-foreground">{summary}</p>
        )}
      </div>

      {canSwitch &&
        (isCurrentCompany ? (
          <Badge
            variant="primary"
            className="shrink-0 gap-1 bg-surface ring-1 ring-primary/20"
          >
            <Check size={12} strokeWidth={3} aria-hidden />
            {t("company:subscription.current")}
          </Badge>
        ) : (
          <Button
            size="xs"
            variant="outline-primary"
            className="shrink-0 transition-all group-hover:border-primary group-hover:bg-primary-muted active:scale-95"
            onClick={() => onSwitch(id)}
            isLoading={isSwitchingThis}
            disabled={isSwitchingAny}
            aria-label={t("common:chrome.switch_to", { name })}
          >
            {t("common:chrome.switch")}
          </Button>
        ))}
    </li>
  );
};
